import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, MessageSquare, Send, LifeBuoy, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";
import { BrandHomeLink, BrandShield } from "@/components/BrandLogo";

const SUPPORT_EMAIL = "support@getezguard.com";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--vault-app-bg)" }}>
      {/* Nav */}
      <header className="glass-nav sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-3.5 flex items-center justify-between">
          <BrandHomeLink size="md" testid="contact-brand-logo" linkTestid="contact-brand-link" />
          <div className="flex items-center gap-2">
            <ThemeToggle testid="contact-theme-toggle" />
            <Button asChild variant="ghost" className="rounded-xl" data-testid="contact-back-home">
              <Link to="/"><ArrowLeft size={16} className="mr-1.5" /> Back home</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-14 pb-20 lg:pt-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        {/* Left: intro */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-5"
        >
          <div className="inline-flex items-center gap-2 pill mb-5">
            <MessageSquare size={12} /> We're here to help
          </div>
          <h1 className="font-[Outfit] text-4xl sm:text-5xl font-semibold tracking-tight">
            Get in touch.
          </h1>
          <p className="text-[color:var(--vault-text-secondary)] mt-5 text-base sm:text-lg max-w-md leading-relaxed">
            Questions about your vault, billing, emergency access, or security? Send us a note and a real
            person will get back to you.
          </p>

          <div className="mt-8 space-y-4">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="vault-card p-4 flex items-center gap-3 vault-card-hover"
              data-testid="contact-email-link"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: "var(--vault-gradient)" }}>
                <Mail size={18} />
              </div>
              <div>
                <div className="font-[Outfit] font-medium">Email us</div>
                <div className="text-sm text-[color:var(--vault-text-secondary)]">{SUPPORT_EMAIL}</div>
              </div>
            </a>
            <div className="vault-card p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: "var(--vault-gradient)" }}>
                <LifeBuoy size={18} />
              </div>
              <div>
                <div className="font-[Outfit] font-medium">Account & emergency access</div>
                <div className="text-sm text-[color:var(--vault-text-secondary)]">We typically reply within one business day.</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-[color:var(--vault-text-tertiary)] pt-1">
              <Lock size={14} /> We never ask for your master password.
            </div>
          </div>
        </motion.div>

        {/* Right: form */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <div className="vault-card p-6 sm:p-8 grain-overlay">
            {sent ? (
              <div className="text-center py-10" data-testid="contact-success">
                <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center text-white" style={{ background: "var(--vault-success)" }}>
                  <Send size={22} />
                </div>
                <h2 className="font-[Outfit] text-2xl font-semibold mt-5">Message sent</h2>
                <p className="text-[color:var(--vault-text-secondary)] mt-2 max-w-sm mx-auto">
                  Thanks for reaching out. We've received your message and will reply to your email soon.
                </p>
                <Button
                  onClick={() => setSent(false)}
                  className="mt-6 rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white"
                  data-testid="contact-send-another"
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-name">Your name</Label>
                    <Input
                      id="contact-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Doe"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-email">Email</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="jane@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-subject">Subject</Label>
                  <Input
                    id="contact-subject"
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="How can we help?"
                    data-testid="contact-subject-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-message">Message</Label>
                  <Textarea
                    id="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us a bit more…"
                    rows={6}
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white py-6 text-base"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? "Sending…" : "Send message"}
                  <Send size={18} className="ml-2" />
                </Button>
              </form>
            )}
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[color:var(--vault-border)] bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[color:var(--vault-text-tertiary)]">
          <div className="flex items-center gap-2">
            <BrandShield className="w-7 h-7" />
            <span data-testid="contact-footer-copyright">© {new Date().getFullYear()} EZGuard</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-[color:var(--vault-brand)]">Home</Link>
            <Link to="/login" className="hover:text-[color:var(--vault-brand)]">Sign in</Link>
            <Link to="/register" className="hover:text-[color:var(--vault-brand)]">Create account</Link>
          </div>
        </div>
        <div className="border-t border-[color:var(--vault-border)]">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 py-4 text-center text-xs text-[color:var(--vault-text-tertiary)]">
            Website Designed and Built by{" "}
            <a
              href="https://gadgetguymedia.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[color:var(--vault-brand)] hover:underline"
              data-testid="footer-credit-link"
            >
              Gadget Guy Media
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
